import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import FicrPanel from "../components/FicrPanel";
import SponsorStatsPanel from "../components/SponsorStatsPanel";
import BrandingPanel from "../components/BrandingPanel";
import RallyInfoPanel from "../components/RallyInfoPanel";
import ShareButton from "../components/share/ShareButton";
import ShareStatsPanel from "../components/ShareStatsPanel";
import { fmtRange } from "../lib/dates";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Trash2, Plus, ExternalLink, Upload, Wand2 } from "lucide-react";

const inp = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-[#FF1E27]";

export default function GameBuilder() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [g, setG] = useState(null);

  const load = () => axios.get(`${API}/organizer/rallies/${id}/game`).then(r => setG(r.data)).catch(() => { toast.error("Rally non trovato"); nav("/organizer/dashboard"); });
  useEffect(() => { if (loading) return; if (!user) { nav("/login"); return; } load(); }, [loading, user, id]);

  const del = async (coll, itemId) => { await axios.delete(`${API}/organizer/game/${coll}/${itemId}`); load(); };
  const post = async (path, body, msg) => {
    try { await axios.post(`${API}/organizer/rallies/${id}/${path}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (!g) return null;
  return (
    <div className="min-h-screen grain-bg">
      <Navbar variant="pro" />
      <section className="pt-24 pb-16 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="pill-badge mb-2">GAME BUILDER · PLAY RALLY</div>
            <h1 className="font-display font-black text-3xl md:text-4xl" data-testid="builder-title">{g.rally.name}</h1>
            <p className="text-slate-400 text-sm">{g.fans} fan attivi · {g.rally.published ? "PUBBLICATO" : "BOZZA (pubblica per rendere il gioco visibile)"}</p>
          </div>
          {g.rally.published && <div className="flex flex-wrap gap-2">
            <button className="btn-outline-rl !py-2 !px-4 text-sm" onClick={() => nav(`/paddock/${id}`)} data-testid="open-paddock-btn"><ExternalLink className="w-4 h-4 inline mr-1" /> APRI PADDOCK</button>
            <button className="btn-outline-rl !py-2 !px-4 text-sm" onClick={() => nav(`/organizer/rallies/${id}/social-kit`)} data-testid="open-social-kit-btn">SOCIAL MEDIA KIT</button>
            <ShareButton kind="event" variant="solid" className="!py-2 !px-4 text-sm" label="CONDIVIDI EVENTO" testid="organizer-share-event-btn"
              card={{ kicker: "L'OFFICIAL PADDOCK È APERTO", lines: [g.rally.name, fmtRange(g.rally.start_date, g.rally.end_date) || "", g.rally.location || ""], cta: "ENTRA NEL PADDOCK", rallyLogo: g.rally.logo_url, sponsorLogos: (g.rally.sponsor_logos || []).map(s => s.url) }}
              share={{ target: `/paddock/${id}`, title: `${g.rally.name} · Official Paddock`, description: "Entra, partecipa al FantaRally, completa le missioni e scala la classifica.", rally_id: id }}
              caption={`L'Official Paddock del ${g.rally.name} è aperto! Entra, partecipa al FantaRally, completa le missioni e scala la classifica: [link]`} />
          </div>}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <EntriesPanel g={g} post={post} del={del} rallyId={id} load={load} />
          <FicrPanel g={g} rallyId={id} load={load} />
          <BrandingPanel rallyId={id} onSaved={load} />
          <RallyInfoPanel rallyId={id} onSaved={load} />
          <ShareStatsPanel rallyId={id} />
          <StagesPanel g={g} post={post} del={del} />
          <QuizPanel g={g} post={post} del={del} rallyId={id} load={load} />
          <SponsorStatsPanel rallyId={id} refreshKey={g.quizzes.length + g.missions.length} />
          <PredictionsPanel g={g} post={post} del={del} load={load} rallyId={id} />
          <MissionsPanel g={g} post={post} del={del} />
        </div>
      </section>
    </div>
  );
}

const Row = ({ children, onDel, testid }) => (
  <div className="flex items-center justify-between border border-white/5 rounded-lg px-3 py-2 text-sm" data-testid={testid}>
    <div>{children}</div>
    <button onClick={onDel} className="text-slate-500 hover:text-[#FF1E27]" data-testid={`${testid}-del`}><Trash2 className="w-4 h-4" /></button>
  </div>
);

function EntriesPanel({ g, post, del, rallyId, load }) {
  const [f, setF] = useState({ number: "", driver: "", codriver: "", car: "", team: "", class_name: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/organizer/rallies/${rallyId}/entries/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info(`File letto (${data.source_chars} caratteri). Claude sta estraendo gli iscritti…`);
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const { data: job } = await axios.get(`${API}/organizer/entries/import/${data.job_id}`);
        if (job.status === "DONE") {
          if (!job.entries.length) toast.error(job.error || "Nessun iscritto riconosciuto nel file");
          else { setPreview(job.entries); toast.success(`${job.entries.length} iscritti riconosciuti da ${file.name}`); }
          return;
        }
        if (job.status === "FAILED") { toast.error(job.error || "Importazione fallita"); return; }
      }
      toast.error("Tempo scaduto, riprova");
    } catch (e) { toast.error(e.response?.data?.detail || "Importazione fallita"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const confirm = async (replace) => {
    try {
      const { data } = await axios.post(`${API}/organizer/rallies/${rallyId}/entries/bulk`, { entries: preview, replace });
      toast.success(`${data.inserted} equipaggi importati`); setPreview(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const classes = [...new Set(g.entries.map(e => e.class_name).filter(Boolean))];

  return (
    <div className="rl-card p-6 lg:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display font-bold text-xl">Equipaggi ({g.entries.length}){classes.length > 0 && <span className="text-xs text-slate-500 font-mono-rl ml-3">{classes.length} CLASSI</span>}</h2>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xlsm,.csv,.txt" className="hidden" data-testid="entries-file-input" onChange={e => upload(e.target.files[0])} />
          <button className="btn-crimson !py-2 !px-4 text-xs" disabled={busy} onClick={() => fileRef.current.click()} data-testid="import-entries-btn">
            <Upload className="w-4 h-4 inline mr-1" />{busy ? "CLAUDE STA LEGGENDO…" : "IMPORTA ELENCO ISCRITTI (PDF / EXCEL)"}</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Carica l'elenco iscritti ufficiale della gara (PDF o Excel): l'AI estrae numero, pilota, navigatore, vettura e classe. Le classi abilitano i pronostici per categoria.</p>

      {preview && (
        <div className="border border-[#FF9F0A]/30 rounded-lg p-4 mb-4" data-testid="entries-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Anteprima · {preview.length} iscritti</div>
            <button className="text-xs text-slate-500 hover:text-white" onClick={() => setPreview(null)} data-testid="entries-preview-discard">Scarta</button>
          </div>
          <div className="max-h-56 overflow-y-auto text-xs font-mono-rl space-y-1">
            {preview.map((e, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 border-b border-white/5 py-1">
                <span className="col-span-1 text-[#FF1E27]">#{e.number}</span>
                <span className="col-span-4 truncate text-white">{e.driver}{e.codriver ? ` / ${e.codriver}` : ""}</span>
                <span className="col-span-4 truncate text-slate-400">{e.car}{e.team ? ` · ${e.team}` : ""}</span>
                <span className="col-span-3 truncate text-[#FF9F0A]">{e.class_name || "—"}{e.group ? ` (${e.group})` : ""}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button className="btn-crimson text-sm flex-1" onClick={() => confirm(true)} data-testid="entries-confirm-replace">SOSTITUISCI ELENCO</button>
            <button className="btn-outline-rl text-sm flex-1" onClick={() => confirm(false)} data-testid="entries-confirm-append">AGGIUNGI AGLI ESISTENTI</button>
          </div>
        </div>
      )}

      <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
        {g.entries.map(e => <Row key={e.id} onDel={() => del("entries", e.id)} testid={`entry-${e.number}`}>
          <b className="text-[#FF1E27] font-mono-rl mr-2">#{e.number}</b>{e.driver}{e.codriver ? ` / ${e.codriver}` : ""} <span className="text-slate-500 text-xs">{e.car}{e.team ? ` · ${e.team}` : ""}</span>
          {e.class_name && <span className="ml-2 pill-badge pill-amber !py-0 !px-2 !text-[9px]">{e.class_name}</span>}
        </Row>)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <input className={inp} placeholder="N°" type="number" value={f.number} onChange={e => setF({ ...f, number: e.target.value })} data-testid="entry-number" />
        <input className={inp} placeholder="Pilota" value={f.driver} onChange={e => setF({ ...f, driver: e.target.value })} data-testid="entry-driver" />
        <input className={inp} placeholder="Navigatore" value={f.codriver} onChange={e => setF({ ...f, codriver: e.target.value })} data-testid="entry-codriver" />
        <input className={inp} placeholder="Auto" value={f.car} onChange={e => setF({ ...f, car: e.target.value })} data-testid="entry-car" />
        <input className={inp} placeholder="Classe (es. Rally2)" value={f.class_name} onChange={e => setF({ ...f, class_name: e.target.value })} data-testid="entry-class" />
      </div>
      <button className="btn-outline-rl w-full mt-3 text-sm" disabled={!f.number || !f.driver} data-testid="add-entry-btn"
        onClick={() => { post("entries", { ...f, number: parseInt(f.number) }, "Equipaggio aggiunto"); setF({ number: "", driver: "", codriver: "", car: "", team: "", class_name: "" }); }}><Plus className="w-4 h-4 inline" /> AGGIUNGI MANUALMENTE</button>
    </div>
  );
}

function StagesPanel({ g, post, del }) {
  const [f, setF] = useState({ name: "", km: "" });
  return (
    <div className="rl-card p-6">
      <h2 className="font-display font-bold text-xl mb-4">Prove Speciali ({g.stages.length})</h2>
      <div className="space-y-2 mb-4 max-h-56 overflow-y-auto">
        {g.stages.map(s => <Row key={s.id} onDel={() => del("stages", s.id)} testid={`stage-${s.order}`}>{s.name} <span className="text-slate-500 text-xs">{s.km} km</span></Row>)}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <input className={`${inp} col-span-2`} placeholder="Nome PS" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="stage-name" />
        <input className={inp} placeholder="Km" type="number" value={f.km} onChange={e => setF({ ...f, km: e.target.value })} data-testid="stage-km" />
      </div>
      <button className="btn-outline-rl w-full mt-3 text-sm" disabled={!f.name} data-testid="add-stage-btn"
        onClick={() => { post("stages", { name: f.name, km: parseFloat(f.km || 0), order: g.stages.length + 1 }, "PS aggiunta"); setF({ name: "", km: "" }); }}><Plus className="w-4 h-4 inline" /> AGGIUNGI</button>
    </div>
  );
}

function QuizPanel({ g, post, del, rallyId, load }) {
  const [f, setF] = useState({ topic: "", n_questions: 5, sponsor: "", language: "it" });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/organizer/rallies/${rallyId}/quizzes/generate`, f);
      setDraft(data); toast.success(`${data.questions.length} domande generate con Claude`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generazione fallita"); } finally { setBusy(false); }
  };
  const genStages = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/organizer/rallies/${rallyId}/quizzes/generate-stages`, { n_questions: f.n_questions, sponsor: f.sponsor });
      toast.success(`${data.created} quiz creati (uno per PS)${data.skipped_existing ? ` · ${data.skipped_existing} PS avevano già un quiz` : ""}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generazione fallita"); } finally { setBusy(false); }
  };
  const save = async () => {
    await post("quizzes", { title: draft.title, sponsor: f.sponsor, questions: draft.questions, seconds_per_question: 15, points_per_correct: 100 }, "Quiz pubblicato");
    setDraft(null);
  };
  return (
    <div className="rl-card p-6 lg:col-span-2">
      <h2 className="font-display font-bold text-xl mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FF9F0A]" /> Quiz AI ({g.quizzes.length})</h2>
      <p className="text-xs text-slate-500 mb-4">Generati con Claude Sonnet 5 · quiz a tempo, 15s per domanda</p>
      <div className="space-y-2 mb-4">
        {g.quizzes.map(q => <Row key={q.id} onDel={() => del("quizzes", q.id)} testid={`quiz-${q.id}`}>{q.title} <span className="text-slate-500 text-xs">{q.questions.length} domande{q.sponsor ? ` · ${q.sponsor}` : ""}{q.stage_id ? " · PS" : ""}</span></Row>)}
      </div>
      <div className="grid md:grid-cols-4 gap-2">
        <input className={`${inp} md:col-span-2`} placeholder="Argomento (es. storia del Rally di Sardegna, Lancia Delta)" value={f.topic} onChange={e => setF({ ...f, topic: e.target.value })} data-testid="quiz-topic" />
        <input className={inp} placeholder="Sponsor" value={f.sponsor} onChange={e => setF({ ...f, sponsor: e.target.value })} data-testid="quiz-sponsor" />
        <select className={inp} value={f.n_questions} onChange={e => setF({ ...f, n_questions: parseInt(e.target.value) })} data-testid="quiz-n">
          {[3, 5, 8, 10].map(n => <option key={n} value={n}>{`${n} domande`}</option>)}
        </select>
      </div>
      <button className="btn-crimson w-full mt-3 text-sm" onClick={gen} disabled={busy} data-testid="generate-quiz-btn">
        {busy ? "CLAUDE STA SCRIVENDO…" : "GENERA QUIZ CON AI"}</button>
      <button className="btn-outline-rl w-full mt-2 text-sm" onClick={genStages} disabled={busy || g.stages.length === 0} data-testid="generate-stage-quizzes-btn">
        <Wand2 className="w-4 h-4 inline mr-1" />{busy ? "…" : `GENERA UN QUIZ PER OGNI PS (${g.stages.length})`}</button>
      {draft && (
        <div className="mt-5 border border-[#FF9F0A]/30 rounded-lg p-4" data-testid="quiz-draft">
          <input className={`${inp} mb-3 font-display font-bold`} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} data-testid="quiz-draft-title" />
          <ol className="space-y-2 text-sm list-decimal pl-5 max-h-64 overflow-y-auto">
            {draft.questions.map((q, i) => (
              <li key={i}><div className="font-semibold">{q.question}</div>
                <div className="text-xs text-slate-400">{q.options.map((o, j) => <span key={j} className={j === q.correct_index ? "text-[#30D158] mr-3" : "mr-3"}>{"ABCD"[j]}. {o}</span>)}</div></li>
            ))}
          </ol>
          <div className="flex gap-2 mt-4">
            <button className="btn-crimson text-sm flex-1" onClick={save} data-testid="save-quiz-btn">PUBBLICA QUIZ</button>
            <button className="btn-outline-rl text-sm" onClick={() => setDraft(null)} data-testid="discard-quiz-btn">SCARTA</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PredictionsPanel({ g, post, del, load, rallyId }) {
  const [f, setF] = useState({ title: "", kind: "STAGE", stage_id: "", class_name: "", points: 300 });
  const classes = [...new Set(g.entries.map(e => e.class_name).filter(Boolean))];
  const settle = async (p, entryId) => {
    if (!entryId) return;
    try { const { data } = await axios.put(`${API}/organizer/predictions/${p.id}/result`, { winner_entry_id: entryId }); toast.success(`Risultato salvato · ${data.winners} fan premiati`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const auto = async () => {
    try { const { data } = await axios.post(`${API}/organizer/rallies/${rallyId}/predictions/auto`); toast.success(`${data.created} pronostici FANTARALLY creati`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const nameOf = id => { const e = g.entries.find(x => x.id === id); return e ? `#${e.number} ${e.driver}` : "—"; };
  const optionsFor = p => p.kind === "CLASS" ? g.entries.filter(e => e.class_name === p.class_name) : g.entries;
  const kindLabel = p => p.kind === "RALLY" ? "ASSOLUTA" : p.kind === "PODIUM" ? "PODIO" : p.kind === "CLASS" ? `CLASSE ${p.class_name}` : "PS";
  const [podium, setPodium] = useState({});
  const settlePodium = async (p) => {
    const ids = podium[p.id] || [];
    if (ids.filter(Boolean).length < 3) return toast.error("Scegli i 3 equipaggi del podio");
    try { const { data } = await axios.put(`${API}/organizer/predictions/${p.id}/result`, { top3_entry_ids: ids }); toast.success(`Podio salvato · ${data.winners} fan premiati`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="rl-card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-bold text-xl">FANTARALLY ({g.predictions.length})</h2>
        <button className="btn-crimson !py-1.5 !px-3 text-xs" onClick={auto} data-testid="auto-pred-btn"><Wand2 className="w-3 h-3 inline mr-1" />GENERA AUTOMATICI</button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Crea in un click i pronostici per classifica assoluta, podio, ogni PS e ogni classe. Con il collegamento FICR si chiudono da soli ogni 15 minuti (e a ogni apertura del Live da parte dei fan).</p>
      <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
        {g.predictions.map(p => (
          <div key={p.id} className="border border-white/5 rounded-lg px-3 py-2 text-sm" data-testid={`pred-${p.id}`}>
            <div className="flex items-center justify-between">
              <div><span className="pill-badge !py-0 !px-2 !text-[9px] mr-2">{kindLabel(p)}</span>{p.title} <span className="text-slate-500 text-xs">{p.points} pt · {p.status === "OPEN" ? "APERTO" : "CHIUSO"}</span></div>
              <button onClick={() => del("predictions", p.id)} className="text-slate-500 hover:text-[#FF1E27]" data-testid={`pred-${p.id}-del`}><Trash2 className="w-4 h-4" /></button>
            </div>
            {p.status === "OPEN" ? (p.kind === "PODIUM" ? (
              <div className="mt-2 grid grid-cols-3 gap-1">
                {[0, 1, 2].map(i => (
                  <select key={i} className={inp} value={(podium[p.id] || [])[i] || ""} data-testid={`settle-podium-${p.id}-${i + 1}`}
                    onChange={e => { const a = [...(podium[p.id] || ["", "", ""])]; a[i] = e.target.value; setPodium({ ...podium, [p.id]: a }); }}>
                    <option value="">{i + 1}° posto…</option>
                    {g.entries.map(e => <option key={e.id} value={e.id}>#{e.number} {e.driver}</option>)}
                  </select>
                ))}
                <button className="btn-outline-rl !py-1.5 text-xs col-span-3" onClick={() => settlePodium(p)} data-testid={`settle-podium-save-${p.id}`}>SALVA PODIO</button>
              </div>
            ) : (
              <select className={`${inp} mt-2`} defaultValue="" onChange={e => settle(p, e.target.value)} data-testid={`settle-${p.id}`}>
                <option value="">Imposta vincitore…</option>
                {optionsFor(p).map(e => <option key={e.id} value={e.id}>#{e.number} {e.driver}</option>)}
              </select>
            )) : <div className="text-xs text-[#FF9F0A] mt-1">{p.kind === "PODIUM" && p.top3_entry_ids ? `Podio: ${p.top3_entry_ids.map(nameOf).join(" · ")}` : `Vincitore: ${nameOf(p.winner_entry_id)}`}{p.settled_by?.startsWith("FICR") && <span className="ml-2 text-[#30D158]">· tempi ufficiali FICR</span>}</div>}
          </div>
        ))}
      </div>
      <input className={inp} placeholder="Titolo (es. Chi vince la PS2?)" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="pred-title" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
        <select className={inp} value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} data-testid="pred-kind"><option value="STAGE">Prova Speciale</option><option value="RALLY">Assoluta</option><option value="PODIUM">Podio (top 3)</option><option value="CLASS">Classe</option></select>
        {f.kind === "STAGE" && <select className={inp} value={f.stage_id} onChange={e => setF({ ...f, stage_id: e.target.value })} data-testid="pred-stage"><option value="">PS…</option>{g.stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}
        {f.kind === "CLASS" && <select className={inp} value={f.class_name} onChange={e => setF({ ...f, class_name: e.target.value })} data-testid="pred-class"><option value="">Classe…</option>{classes.map(c => <option key={c} value={c}>{c}</option>)}</select>}
        <input className={inp} type="number" value={f.points} onChange={e => setF({ ...f, points: parseInt(e.target.value) })} data-testid="pred-points" />
      </div>
      <button className="btn-outline-rl w-full mt-3 text-sm" disabled={!f.title || (f.kind === "CLASS" && !f.class_name)} data-testid="add-pred-btn"
        onClick={() => { post("predictions", { ...f, stage_id: f.stage_id || null, class_name: f.class_name || null }, "Pronostico aperto"); setF({ title: "", kind: "STAGE", stage_id: "", class_name: "", points: 300 }); }}><Plus className="w-4 h-4 inline" /> APRI PRONOSTICO</button>
    </div>
  );
}

function MissionsPanel({ g, post, del }) {
  const [f, setF] = useState({ title: "", sponsor: "", description: "", secret_code: "", points: 200 });
  return (
    <div className="rl-card p-6">
      <h2 className="font-display font-bold text-xl mb-4">Missioni Sponsor ({g.missions.length})</h2>
      <div className="space-y-2 mb-4 max-h-56 overflow-y-auto">
        {g.missions.map(m => <Row key={m.id} onDel={() => del("missions", m.id)} testid={`mission-${m.id}`}>{m.title} <span className="text-slate-500 text-xs">{m.sponsor} · codice <b className="font-mono-rl text-[#00F0FF]">{m.secret_code}</b> · {m.points} pts</span></Row>)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className={inp} placeholder="Titolo" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="mission-title" />
        <input className={inp} placeholder="Sponsor" value={f.sponsor} onChange={e => setF({ ...f, sponsor: e.target.value })} data-testid="mission-sponsor" />
        <input className={`${inp} col-span-2`} placeholder="Descrizione (dove trovare il QR)" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="mission-desc" />
        <input className={`${inp} font-mono-rl`} placeholder="CODICE SEGRETO" value={f.secret_code} onChange={e => setF({ ...f, secret_code: e.target.value.toUpperCase() })} data-testid="mission-code" />
        <input className={inp} type="number" value={f.points} onChange={e => setF({ ...f, points: parseInt(e.target.value) })} data-testid="mission-points" />
      </div>
      <button className="btn-outline-rl w-full mt-3 text-sm" disabled={!f.title || !f.secret_code} data-testid="add-mission-btn"
        onClick={() => { post("missions", f, "Missione creata"); setF({ title: "", sponsor: "", description: "", secret_code: "", points: 200 }); }}><Plus className="w-4 h-4 inline" /> CREA MISSIONE</button>
    </div>
  );
}
